// src/utils/category-helpers.ts

/**
 * Utility functions for transforming category data from API to component format
 */

import { ICategoryProduct } from "../models/state/IStoreProduct";
import { ICategory } from "../models/state/IStoreTreatment";

export const transformCategoryData = (rawData: any[]): ICategory[] => {
    // Ensure rawData is an array and not null/undefined
    if (!rawData || !Array.isArray(rawData)) {
        return [];
    }

    // Create a map for quick lookup
    const categoryMap = new Map();

    // create all category objects and store in map
    rawData.forEach(item => {
        // Skip any null or non-object items
        if (!item || typeof item !== 'object') {
            return;
        }

        const id = item.CategoryId != null ? String(item.CategoryId) : null;
        if (id === null) {
            return;
        }

        // Check and convert ParentCategoryId safely
        const parentId = item.ParentCategoryId != null
            ? String(item.ParentCategoryId)
            : null;

        categoryMap.set(id, {
            categoryId: id,
            id: item.Id,
            name: item.CategoryName || "Unnamed Category",
            level: 0, // Will calculate level later
            parentId: parentId,
            subCategories: [],
            orderId: item.OrderId || 0,
            treatments: [], // Will be populated later when treatments are fetched
        });
    });

    // build the tree structure
    const rootCategories: ICategory[] = [];

    categoryMap.forEach((category, id) => {
        // Add to its parent's subcategories if it has a parent
        if (category.parentId && category.parentId !== "0") {
            const parent = categoryMap.get(category.parentId);
            if (parent) {
                parent.subCategories.push(category);
            } else {
                // If parent doesn't exist, treat as root
                rootCategories.push(category);
            }
        }
        // Otherwise it's a root category
        else if (category.parentId === "0" || category.parentId === null) {
            rootCategories.push(category);
        }
    });

    // Sort categories by OrderId at each level
    const sortCategories = (categories: ICategory[]): ICategory[] => {
        if (!categories || !Array.isArray(categories)) return [];

        // Sort current level by orderId
        const sorted = [...categories].sort((a, b) => a.orderId - b.orderId);

        // Recursively sort subcategories
        sorted.forEach(category => {
            if (category.subCategories && category.subCategories.length > 0) {
                category.subCategories = sortCategories(category.subCategories);
            }
        });

        return sorted;
    };

    // Sort the root categories and all their descendants
    const sortedRootCategories = sortCategories(rootCategories);

    // calculate levels for all categories (after sorting to maintain correct levels)
    const calculateLevels = (categories: ICategory[], level: number) => {
        if (!categories || !Array.isArray(categories)) return;

        categories.forEach(category => {
            category.level = level;
            if (category.subCategories && category.subCategories.length > 0) {
                calculateLevels(category.subCategories, level + 1);
            }
        });
    };

    calculateLevels(sortedRootCategories, 0);

    return sortedRootCategories;
};

export const resetCategoriesTreatments = (
    categories: ICategory[]
): ICategory[] => {
    return categories.map((category) => ({
        ...category,
        treatments: [], // Reset treatments for current category
        subCategories: resetCategoriesTreatments(category.subCategories), // Recursive call for subcategories
    }));
};
export const resetCategoriesProducts = (
    categories: ICategoryProduct[]
): ICategoryProduct[] => {
    return categories.map((category) => ({
        ...category,
        products: [], // Reset products for current category
        subCategories: resetCategoriesProducts(category.subCategories), // Recursive call for subcategories
    }));
};

export const transformCategoryDataProduct = (rawData: any[]): ICategoryProduct[] => {
    // Ensure rawData is an array and not null/undefined
    if (!rawData || !Array.isArray(rawData)) {
        return [];
    }

    // Create a map for quick lookup
    const categoryMap = new Map();

    // create all category objects and store in map
    rawData.forEach(item => {
        // Skip any null or non-object items
        if (!item || typeof item !== 'object') {
            return;
        }

        const id = item.CategoryId != null ? String(item.CategoryId) : null;
        if (id === null) {
            return;
        }

        // Check and convert ParentCategoryId safely
        const parentId = item.ParentCategoryId != null
            ? String(item.ParentCategoryId)
            : null;

        categoryMap.set(id, {
            id: item.Id,
            categoryId: id,
            name: item.CategoryName || "Unnamed Category",
            level: 0, // Will calculate level later
            parentId: parentId,
            subCategories: [],
            orderId: item.OrderId || 0,
            products: [], // Will be populated later when products are fetched
        });
    });

    // build the tree structure
    const rootCategories: ICategoryProduct[] = [];

    categoryMap.forEach((category, id) => {
        // Add to its parent's subcategories if it has a parent
        if (category.parentId && category.parentId !== "0") {
            const parent = categoryMap.get(category.parentId);
            if (parent) {
                parent.subCategories.push(category);
            } else {
                // If parent doesn't exist, treat as root
                rootCategories.push(category);
            }
        }
        // Otherwise it's a root category
        else if (category.parentId === "0" || category.parentId === null) {
            rootCategories.push(category);
        }
    });

    // Sort categories by OrderId at each level
    const sortCategories = (categories: ICategoryProduct[]): ICategoryProduct[] => {
        if (!categories || !Array.isArray(categories)) return [];

        // Sort current level by orderId
        const sorted = [...categories].sort((a, b) => a.orderId - b.orderId);

        // Recursively sort subcategories
        sorted.forEach(category => {
            if (category.subCategories && category.subCategories.length > 0) {
                category.subCategories = sortCategories(category.subCategories);
            }
        });

        return sorted;
    };

    // Sort the root categories and all their descendants
    const sortedRootCategories = sortCategories(rootCategories);

    // calculate levels for all categories (after sorting to maintain correct levels)
    const calculateLevels = (categories: ICategoryProduct[], level: number) => {
        if (!categories || !Array.isArray(categories)) return;

        categories.forEach(category => {
            category.level = level;
            if (category.subCategories && category.subCategories.length > 0) {
                calculateLevels(category.subCategories, level + 1);
            }
        });
    };

    calculateLevels(sortedRootCategories, 0);

    return sortedRootCategories;
};


// Function to add treatments to the appropriate subcategory
export function addTreatmentsToCategories(
    categories: ICategory[],
    categoryId: string,
    treatments: any[]
): ICategory[] {
    return categories.map(category => {
        // Check if this category contains the target subcategory
        const subCategoryIndex = category.subCategories.findIndex(
            subCat => subCat.id === categoryId
        );

        if (subCategoryIndex !== -1) {
            // Found the subcategory, update its treatments
            const updatedSubCategories = [...category.subCategories];
            updatedSubCategories[subCategoryIndex] = {
                ...updatedSubCategories[subCategoryIndex],
                treatments
            };

            return {
                ...category,
                subCategories: updatedSubCategories
            };
        }

        return category;
    });
}


export function countTotalTreatments(category: ICategory): number {
    // Count treatments in current category
    let count = category.treatments.length;

    // Recursively count treatments in all subcategories
    for (const subCategory of category.subCategories) {
        count += countTotalTreatments(subCategory);
    }
    return count;
}

export function getDefaultActiveKeys(categories: ICategory[]): string[] {
    const activeKeys: string[] = [];

    categories.forEach((category, index) => {
        // Check if current category or any subcategory has treatments
        const hasTreatments =
            category.treatments.length > 0 ||
            hasTreatmentsInSubcategories(category.subCategories);

        if (hasTreatments) {
            activeKeys.push(`${Number(category.parentId) ? category.parentId : "root"}-${index}-${Number(category.categoryId) ? category.categoryId : "0"}`);
        }

        // Always process subcategories to find any with treatments
        if (category.subCategories.length > 0) {
            const subActiveKeys = getDefaultActiveKeys(category.subCategories);
            activeKeys.push(...subActiveKeys);
        }
    });

    return activeKeys;
}

// Helper function to check if any subcategory has treatments
function hasTreatmentsInSubcategories(subCategories: ICategory[]): boolean {
    return subCategories.some(subCategory =>
        subCategory.treatments.length > 0 ||
        hasTreatmentsInSubcategories(subCategory.subCategories)
    );
}
export function getDefaultActiveKeysProduct(categories: ICategoryProduct[]): string[] {
    const activeKeys: string[] = [];

    categories.forEach((category, index) => {
        // Check if current category or any subcategory has treatments
        const hasTreatments =
            category.products.length > 0 ||
            hasTreatmentsInSubcategoriesProduct(category.subCategories);

        if (hasTreatments) {
            activeKeys.push(`${Number(category.parentId) ? category.parentId : "root"}-${index}-${Number(category.categoryId) ? category.categoryId : "0"}`);
        }

        // Always process subcategories to find any with treatments
        if (category.subCategories.length > 0) {
            const subActiveKeys = getDefaultActiveKeysProduct(category.subCategories);
            activeKeys.push(...subActiveKeys);
        }
    });

    return activeKeys;
}

// Helper function to check if any subcategory has treatments
function hasTreatmentsInSubcategoriesProduct(subCategories: ICategoryProduct[]): boolean {
    return subCategories.some(subCategory =>
        subCategory.products.length > 0 ||
        hasTreatmentsInSubcategoriesProduct(subCategory.subCategories)
    );
}